import React, { useEffect } from 'react';
import { NumberField, formatCprNumber } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { Cprfield as CprfieldType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const Cprfield = ({ elementId, caption, placeholder, helpText }: CprfieldType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = Cprfield.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = Cprfield.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <NumberField
                value={value}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                onValueChange={(valueObject) => {
                    const value = valueObject.value;
                    setFormValue(elementId, value);
                }}
                isValid={isValid}
                errorText={errorMessage}
                id={elementId}
                key={elementId}
                label={placeholder}
                helpText={helpText}
                required={isRequiredField}
                aria-label={caption}
                data-testid="cprfield"
                maxLength={10}
                allowLeadingZeros={true}
            />
        </>
    );
};

Cprfield.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as CprfieldType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={formatCprNumber(value)} />;
};

Cprfield.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Cprfield.getValue(formValues, elementId);
    const valueStr = Cprfield.getValueHuman(formValues, elementId);

    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('cprfield', elementId, label, valueStr, valueId);
};

Cprfield.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Cprfield.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Cprfield.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

Cprfield.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = Cprfield.getValue(formValues, elementId);

    return value;
};

Cprfield.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

Cprfield.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHadFocusAtLeastOnce) {
        return undefined;
    }

    //const value = Cprfield.getValue(formValues, elementId);

    // When the user has not typed anything,
    // undefined is returned to prevent
    // showing red errors all over as soon
    // as he sees the form
    // if (value === '' || value === undefined) {
    //     return undefined;
    // }

    const isValid = Cprfield.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Cprfield;
